import { useViewerStore, ToolName, LayoutType, ViewerMode } from '../stores/viewerStore';
import {
  cornerstoneTools,
  TOOLGROUP_ID,
  TOOLGROUP_3D_ID,
  RENDERING_ENGINE_ID,
  RenderingEngine,
} from '../cornerstone/init';
import { getRenderingEngine } from '@cornerstonejs/core';
import {
  IconWL, IconPan, IconZoom, IconMagnify, IconScroll, IconCine,
  IconProbe, IconLength, IconAngle, IconCobb, IconRect, IconEllipse, IconArrow,
  IconCrosshairs, IconInvert, IconFlipH, IconFlipV, IconRotateL, IconRotateR,
  IconReset, IconClear, IconOverlay, IconLayout, IconMPR, Icon3D, IconMIP,
  IconBack, IconSync, IconMonitor, IconZoomFit, IconZoomActual,
  IconDownload, IconSave, IconStar, IconPriors,
  IconReport, IconProtocols, IconBasketAdd, IconPrint,
} from './ToolIcons';
import { zoomToFit, zoomToActual, exportAsPng, exportAsJpeg, downloadDicom } from '../utils/viewportActions';
import { WL_PRESETS } from '../utils/wlPresets';
import { saveAnnotations } from '../utils/annotations';
import { getScreens, openNewViewerWindow, openOnAllScreens, isMultiScreenSupported, ScreenInfo } from '../utils/multiMonitor';
import { getToolbarButtons, type PluginContext } from '../plugins';
import styles from './Toolbar.module.css';
import { useState, useEffect, useRef, useCallback } from 'react';

const { Enums: csToolsEnums, ToolGroupManager, annotation } = cornerstoneTools;

const VOLUME_3D_PRESETS = [
  'CT-Bone',
  'CT-Soft-Tissue',
  'CT-Lung',
  'CT-Fat',
  'CT-Coronary-Arteries',
  'CT-Chest-Contrast-Enhanced',
  'CT-Chest-Vessels',
  'CT-AAA',
  'CT-MIP',
  'MR-Default',
  'MR-MIP',
];

const LAYOUTS: LayoutType[] = [
  '1x1', '1x2', '1x3',
  '2x1', '2x2', '2x3',
  '3x1', '3x2', '3x3',
];

const TOOL_MAPPING: Record<string, string> = {
  Wwwc: 'WindowLevel',
  Pan: 'Pan',
  Zoom: 'Zoom',
  Magnify: 'Magnify',
  StackScroll: 'StackScroll',
  Probe: 'Probe',
  Length: 'Length',
  Angle: 'Angle',
  CobbAngle: 'CobbAngle',
  RectangleROI: 'RectangleROI',
  EllipticalROI: 'EllipticalROI',
  ArrowAnnotate: 'ArrowAnnotate',
  Crosshairs: 'Crosshairs',
};

interface Props {
  onBack: () => void;
  studyId?: number;
  modality?: string;
  onOpenProtocols?: () => void;
  /** Toggle-mark the currently-displayed image as a key image */
  onToggleKeyImage?: () => void;
  /** True if the currently-displayed image is already a key image (paints the button active) */
  isCurrentImageKey?: boolean;
  /** Add the currently-active viewport's image to the composer basket. */
  onAddToComposerBasket?: () => void;
  /** Dispatch every basket item to the EdgePACS Companion via edgeprint://. */
  onSendComposerBasket?: () => void;
  /** Wipe the composer basket. */
  onClearComposerBasket?: () => void;
  /** Number of items currently in the basket — drives the badge on the Send button. */
  composerBasketCount?: number;
  /** Passed to plugin toolbar buttons so they can open modals + reach the
   *  active viewport. Constructed once in Viewer.tsx. */
  pluginCtx?: PluginContext;
}

export default function Toolbar({
  onBack, studyId, modality, onOpenProtocols,
  onToggleKeyImage, isCurrentImageKey,
  onAddToComposerBasket, onSendComposerBasket, onClearComposerBasket,
  composerBasketCount = 0,
  pluginCtx,
}: Props) {
  const measurementPluginButtons = pluginCtx ? getToolbarButtons('measurement') : [];
  const {
    activeTool, layout, viewerMode, overlaysVisible,
    syncWL, syncScroll, syncCamera,
    setActiveTool, setLayout, setViewerMode, toggleOverlays,
    toggleSyncWL, toggleSyncScroll, toggleSyncCamera,
  } = useViewerStore();
  const [showWLPresets, setShowWLPresets] = useState(false);
  const [showLayouts, setShowLayouts] = useState(false);
  const [showMoreTools, setShowMoreTools] = useState(false);
  const [showMonitors, setShowMonitors] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [exportWithAnnotations, setExportWithAnnotations] = useState(false);
  const [showSync, setShowSync] = useState(false);
  const [showMode, setShowMode] = useState(false);
  const [showMoreDrawer, setShowMoreDrawer] = useState(false);
  const [screens, setScreens] = useState<ScreenInfo[]>([]);
  const toolbarRef = useRef<HTMLDivElement>(null);

  // Close all dropdowns on outside click
  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (toolbarRef.current && !toolbarRef.current.contains(e.target as Node)) {
        setShowWLPresets(false);
        setShowLayouts(false);
        setShowMonitors(false);
        setShowExport(false);
        setShowSync(false);
        setShowMode(false);
        setShowMoreDrawer(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  // Auto-revert activeTool when the mode change makes it invalid. Right now
  // the only tool that's mode-restricted is Crosshairs (MPR/3D only). If the
  // user had it active and dropped to stack, the button vanishes and the
  // Primary mouse binding would be left pointing at nothing — so fall back
  // to W/L. Calling selectTool re-asserts all the static bindings too.
  useEffect(() => {
    if (viewerMode === 'stack' && activeTool === 'Crosshairs') {
      selectTool('Wwwc');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewerMode]);

  /**
   * Switch the active mouse-Primary tool.
   *
   * Why this is more elaborate than `setToolActive(new); setToolPassive(old)`:
   *
   * - React state (`activeTool`) can lag the real toolGroup state if the user
   *   clicks two buttons in quick succession, so basing "what to deactivate"
   *   on it is unreliable. Instead, force every Primary-eligible tool to
   *   passive first, then activate just the one we want.
   *
   * - `setToolPassive` clears ALL bindings on a tool, including non-Primary
   *   ones. That used to wipe StackScroll's Wheel binding and Pan/Zoom's
   *   Aux/Secondary bindings whenever the user switched tools — so after
   *   picking, say, Length, the wheel stopped scrolling and right-click
   *   stopped zooming. We re-assert those static bindings every switch.
   */
  function selectTool(tool: ToolName) {
    // Toggle: clicking the already-active tool reverts to W/L (the safe
    // default). Lets users get unstuck from Probe / Cine / etc. without
    // hunting for the W/L button. (#8 Cine, #15 Probe-can't-deselect)
    if (tool === activeTool && tool !== 'Wwwc') {
      tool = 'Wwwc';
    }
    setActiveTool(tool);

    const csToolName = TOOL_MAPPING[tool];
    if (!csToolName) return;

    const PRIMARY_TOOLS = [
      'WindowLevel', 'Pan', 'Zoom', 'Magnify', 'StackScroll',
      'Probe', 'Length', 'Angle', 'CobbAngle', 'RectangleROI', 'EllipticalROI',
      'ArrowAnnotate', 'Crosshairs',
    ];

    // Apply across BOTH tool groups. In MPR/3D modes the ortho viewports
    // live on TOOLGROUP_ID and the volume3D pane on TOOLGROUP_3D_ID; if we
    // only touched one, the wheel binding for StackScroll wouldn't get
    // re-asserted on the other and Probe would stay sticky after picking
    // a measurement tool. (#15)
    for (const tgId of [TOOLGROUP_ID, TOOLGROUP_3D_ID]) {
      const tg = ToolGroupManager.getToolGroup(tgId);
      if (!tg) continue;

      for (const t of PRIMARY_TOOLS) {
        if (t === csToolName) continue;
        try { tg.setToolPassive(t); } catch { /* tool not in group */ }
      }

      try {
        tg.setToolActive(csToolName, {
          bindings: [{ mouseButton: csToolsEnums.MouseBindings.Primary }],
        });
      } catch { /* tool may not exist on this group */ }

      // Re-assert static bindings so wheel-scroll, middle-pan and right-zoom
      // keep working regardless of which Primary tool the user picked.
      if (csToolName !== 'Pan') {
        try {
          tg.setToolActive('Pan', {
            bindings: [{ mouseButton: csToolsEnums.MouseBindings.Auxiliary }],
          });
        } catch {}
      }
      if (csToolName !== 'Zoom') {
        try {
          tg.setToolActive('Zoom', {
            bindings: [{ mouseButton: csToolsEnums.MouseBindings.Secondary }],
          });
        } catch {}
      }
      if (csToolName !== 'StackScroll') {
        try {
          tg.setToolActive('StackScroll', {
            bindings: [{ mouseButton: csToolsEnums.MouseBindings.Wheel as any }],
          });
        } catch {}
      }
    }
  }

  function selectToolAndClose(tool: ToolName) {
    selectTool(tool);
    setShowMoreTools(false);
  }

  function applyWLPreset(ww: number, wc: number) {
    setShowWLPresets(false);
    const engine = getRenderingEngine(RENDERING_ENGINE_ID);
    if (!engine) return;

    // Apply to all viewports
    const viewportIds = engine.getViewports().map((vp: any) => vp.id);
    for (const vpId of viewportIds) {
      const vp = engine.getViewport(vpId);
      if (!vp) continue;
      const lower = wc - ww / 2;
      const upper = wc + ww / 2;
      (vp as any).setProperties({ voiRange: { lower, upper } });
      vp.render();
    }
  }

  function apply3DPreset(presetName: string) {
    setShowWLPresets(false);
    const engine = getRenderingEngine(RENDERING_ENGINE_ID);
    if (!engine) return;

    // Find the 3D viewport
    const viewports = engine.getViewports();
    for (const vp of viewports) {
      if ((vp as any).type === 'volume3d' || (vp as any).constructor?.name === 'VolumeViewport3D') {
        try {
          (vp as any).setProperties({ preset: presetName });
          vp.render();
        } catch { /* preset may not exist */ }
      }
    }
  }

  function clearAnnotations() {
    annotation.state.removeAllAnnotations();
    getActiveViewport()?.render();
  }

  function getActiveViewport(): any {
    const engine = getRenderingEngine(RENDERING_ENGINE_ID);
    return engine?.getViewport('viewport-0');
  }

  function flipH() {
    const vp = getActiveViewport();
    if (!vp) return;
    const { flipHorizontal } = vp.getCamera();
    vp.setCamera({ flipHorizontal: !flipHorizontal });
    vp.render();
  }

  function flipV() {
    const vp = getActiveViewport();
    if (!vp) return;
    const { flipVertical } = vp.getCamera();
    vp.setCamera({ flipVertical: !flipVertical });
    vp.render();
  }

  function rotateLeft() {
    const vp = getActiveViewport();
    if (!vp) return;
    const rotation = (vp.getRotation?.() ?? 0) - 90;
    vp.setRotation?.(rotation);
    vp.render();
  }

  function rotateRight() {
    const vp = getActiveViewport();
    if (!vp) return;
    const rotation = (vp.getRotation?.() ?? 0) + 90;
    vp.setRotation?.(rotation);
    vp.render();
  }

  function invertImage() {
    const vp = getActiveViewport();
    if (!vp) return;
    const { invert } = vp.getProperties();
    (vp as any).setProperties({ invert: !invert });
    vp.render();
  }

  function resetViewport() {
    const vp = getActiveViewport();
    if (!vp) return;
    vp.resetCamera();
    (vp as any).resetProperties?.();
    vp.render();
  }

  function toggleMode(mode: ViewerMode, modeLayout: LayoutType) {
    const next = viewerMode === mode ? 'stack' : mode;
    setViewerMode(next);
    setLayout(next === mode ? modeLayout : '1x1');
    setShowMoreTools(false);
  }

  const TB = ({
    tool, icon, label, onClick, isActive, disabled,
  }: {
    tool?: ToolName;
    icon: React.ReactNode;
    label: string;
    onClick?: () => void;
    isActive?: boolean;
    disabled?: boolean;
  }) => {
    const active = isActive ?? (tool ? activeTool === tool : false);
    return (
      <button
        className={`${styles.btn} ${active ? styles.active : ''}`}
        onClick={onClick ?? (() => tool && selectTool(tool))}
        title={label}
        disabled={disabled}
      >
        {icon}
        <span className={styles.btnLabel}>{label}</span>
      </button>
    );
  };

  return (
    <>
      {/* ====== Desktop Toolbar (top bar) ====== */}
      <div className={styles.toolbar} ref={toolbarRef}>
        {/* Closes the viewer tab (window.close() if popout, fallback navigate
            to /worklist). Useful for RIS deep-links: opening a study from a
            RIS must allow the user to release the session when done. */}
        <button className={styles.backBtn} onClick={onBack} title="Close Viewer">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2"
               strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
        <div className={styles.separator} />

        <div className={styles.dropdownContainer}>
          <TB tool="Wwwc" icon={<IconWL />} label="W/L" onClick={() => selectTool('Wwwc')} />
          <button className={styles.dropdownArrow} onClick={() => setShowWLPresets(!showWLPresets)} title="WL Presets">&#9662;</button>
          {showWLPresets && (
            <div className={styles.dropdown}>
              {WL_PRESETS
                .filter((p) => p.modalities.length === 0 || !modality || p.modalities.includes(modality))
                .map((p) => (
                  <button key={p.label} onClick={() => applyWLPreset(p.ww, p.wc)}>
                    {p.label} <span className={styles.presetVal}>({p.ww}/{p.wc})</span>
                  </button>
                ))}
              {viewerMode === 'mpr3d' && (
                <>
                  <div className={styles.dropdownDivider} />
                  <div className={styles.dropdownLabel}>3D Volume Presets</div>
                  {VOLUME_3D_PRESETS.map((p) => (
                    <button key={p} onClick={() => apply3DPreset(p)}>
                      {p}
                    </button>
                  ))}
                </>
              )}
            </div>
          )}
        </div>

        <TB tool="Pan" icon={<IconPan />} label="Pan" />
        <TB tool="Zoom" icon={<IconZoom />} label="Zoom" />
        <TB tool="StackScroll" icon={<IconScroll />} label="Scroll" />
        <TB tool="Cine" icon={<IconCine />} label="Cine" />

        <div className={styles.separator} />

        <TB tool="Length" icon={<IconLength />} label="Length" />
        <TB tool="Angle" icon={<IconAngle />} label="Angle" />
        <TB tool="CobbAngle" icon={<IconCobb />} label="Cobb" />
        <TB tool="RectangleROI" icon={<IconRect />} label="Rect" />
        <TB tool="EllipticalROI" icon={<IconEllipse />} label="Ellipse" />
        <TB tool="ArrowAnnotate" icon={<IconArrow />} label="Arrow" />

        {/* Plugin-contributed measurement buttons (chiro tools, future
            add-ons). Injected via src/plugins/registry.ts; each plugin
            self-registers at module load in main.tsx. */}
        {measurementPluginButtons.map((b) => (
          <TB
            key={b.id}
            icon={b.icon}
            label={b.label}
            onClick={() => pluginCtx && b.onClick(pluginCtx)}
          />
        ))}

        {/* Crosshairs only renders meaningfully across multi-plane volumes —
            in stack (single-series) mode it does nothing, so showing the
            button there is misleading. Same for Sync at 1x1: there's no
            second viewport to sync to. Both hidden when their precondition
            isn't met. */}
        {viewerMode !== 'stack' && (
          <>
            <div className={styles.separator} />
            <TB tool="Crosshairs" icon={<IconCrosshairs />} label="Cross" />
          </>
        )}

        {layout !== '1x1' && (
        <div className={styles.dropdownContainer}>
          <TB
            icon={<IconSync />}
            label="Sync"
            isActive={syncWL || syncScroll || syncCamera}
            onClick={() => setShowSync(!showSync)}
          />
          {showSync && (
            <div className={styles.dropdown}>
              <label style={{ display: 'flex', gap: 6, padding: '6px 10px', cursor: 'pointer' }}>
                <input type="checkbox" checked={syncWL} onChange={toggleSyncWL} />
                Window / Level
              </label>
              <label style={{ display: 'flex', gap: 6, padding: '6px 10px', cursor: 'pointer' }}>
                <input type="checkbox" checked={syncScroll} onChange={toggleSyncScroll} />
                Scroll / Slice
              </label>
              <label
                style={{ display: 'flex', gap: 6, padding: '6px 10px', cursor: 'pointer' }}
                title="Camera sync only applies between viewports showing the same series or the same MPR volume — viewports of different series are skipped."
              >
                <input type="checkbox" checked={syncCamera} onChange={toggleSyncCamera} />
                Zoom / Pan
              </label>
              <div style={{
                padding: '4px 10px 8px', fontSize: 10, color: '#a0aec0',
                lineHeight: 1.4, maxWidth: 200,
              }}>
                Zoom / Pan sync requires viewports of the same series or MPR volume.
              </div>
            </div>
          )}
        </div>
        )}

        <div className={styles.separator} />

        <TB icon={<IconInvert />} label="Invert" onClick={invertImage} />
        <TB icon={<IconReset />} label="Reset" onClick={resetViewport} />
        <TB icon={<IconZoomFit />} label="Fit" onClick={() => zoomToFit()} />

        <div className={styles.separator} />

        {/* Export dropdown */}
        <div className={styles.dropdownContainer}>
          <TB icon={<IconDownload />} label="Export" onClick={() => setShowExport(!showExport)} />
          {showExport && (
            <div className={styles.dropdown}>
              <label
                style={{ display: 'flex', gap: 6, padding: '6px 10px', cursor: 'pointer', fontSize: 12 }}
                onClick={(e) => e.stopPropagation()}
                title="Burn lengths, ROIs, arrows and labels into the exported image"
              >
                <input
                  type="checkbox"
                  checked={exportWithAnnotations}
                  onChange={(e) => setExportWithAnnotations(e.target.checked)}
                />
                Include annotations
              </label>
              <div className={styles.dropdownDivider} />
              <button onClick={() => { exportAsPng('viewport-0', exportWithAnnotations); setShowExport(false); }}>Save as PNG</button>
              <button onClick={() => { exportAsJpeg('viewport-0', exportWithAnnotations); setShowExport(false); }}>Save as JPEG</button>
              <button onClick={() => { downloadDicom(); setShowExport(false); }}>Download DICOM</button>
            </div>
          )}
        </div>

        {/* Save annotations */}
        <TB icon={<IconSave />} label="Save" onClick={() => {
          if (studyId) saveAnnotations(studyId, 0);
        }} />

        <div className={styles.separator} />

        {/* Protocols / Key image. Compare removed in v4.3 — multi-study
            viewer (collapsible side tree) makes a dedicated dialog redundant. */}
        {onOpenProtocols && (
          <TB icon={<IconProtocols />} label="Protocols" onClick={onOpenProtocols} />
        )}
        {onToggleKeyImage && (
          <TB
            icon={<IconStar />}
            label="Key"
            isActive={isCurrentImageKey}
            onClick={onToggleKeyImage}
          />
        )}

        {/* Composer basket. Two buttons:
              +  Add current image to basket
              🖨 Open Composer (label switches to "Push N" when basket is non-empty).
                 Empty-basket click just opens the composer app — the user can
                 add images later or arrange whatever's already staged.
            The little × on the Push button clears the basket. */}
        {onAddToComposerBasket && (
          <TB
            icon={<IconBasketAdd />}
            label="Add"
            onClick={onAddToComposerBasket}
          />
        )}
        {onSendComposerBasket && (
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <TB
              icon={<IconPrint />}
              label={composerBasketCount > 0 ? `Push ${composerBasketCount}` : 'Open'}
              onClick={onSendComposerBasket}
            />
            {composerBasketCount > 0 && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onClearComposerBasket?.(); }}
                title="Clear composer basket"
                style={{
                  position: 'absolute',
                  top: -2, right: -2,
                  width: 16, height: 16, padding: 0,
                  border: 'none', borderRadius: '50%',
                  background: '#c53030', color: '#fff',
                  fontSize: 10, lineHeight: '16px',
                  cursor: 'pointer',
                }}
              >×</button>
            )}
          </div>
        )}

        <div className={styles.separator} />

        <TB icon={<IconOverlay />} label="Overlay" isActive={overlaysVisible} onClick={toggleOverlays} />

        <div className={styles.dropdownContainer}>
          <TB icon={<IconLayout />} label={layout} onClick={() => setShowLayouts(!showLayouts)} />
          {showLayouts && (
            <div className={styles.layoutGrid}>
              {LAYOUTS.map((l) => {
                const [r, c] = l.split('x').map(Number);
                return (
                  <button
                    key={l}
                    className={`${styles.layoutBtn} ${l === layout ? styles.layoutActive : ''}`}
                    onClick={() => { setLayout(l); setShowLayouts(false); }}
                    title={l}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <rect x="1" y="1" width="22" height="22" rx="2" />
                      {Array.from({ length: c - 1 }).map((_, ci) => (
                        <line key={`c${ci}`} x1={1 + ((ci + 1) * 22) / c} y1="1" x2={1 + ((ci + 1) * 22) / c} y2="23" />
                      ))}
                      {Array.from({ length: r - 1 }).map((_, ri) => (
                        <line key={`r${ri}`} x1="1" y1={1 + ((ri + 1) * 22) / r} x2="23" y2={1 + ((ri + 1) * 22) / r} />
                      ))}
                    </svg>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className={styles.separator} />

        {/* Mode dropdown: MPR / 3D / MIP */}
        <div className={styles.dropdownContainer}>
          <TB
            icon={viewerMode === 'mpr3d' ? <Icon3D /> : viewerMode === 'mip' ? <IconMIP /> : <IconMPR />}
            label={viewerMode === 'mpr' ? 'MPR' : viewerMode === 'mpr3d' ? '3D' : viewerMode === 'mip' ? 'MIP' : 'Mode'}
            isActive={viewerMode !== 'stack'}
            onClick={() => setShowMode(!showMode)}
          />
          {showMode && (
            <div className={styles.dropdown}>
              <button
                className={viewerMode === 'mpr' ? styles.dropdownActive : ''}
                onClick={() => { toggleMode('mpr', '1x3'); setShowMode(false); }}
              >MPR (3-plane)</button>
              <button
                className={viewerMode === 'mpr3d' ? styles.dropdownActive : ''}
                onClick={() => { toggleMode('mpr3d', '2x2'); setShowMode(false); }}
              >3D (MPR + Volume)</button>
              <button
                className={viewerMode === 'mip' ? styles.dropdownActive : ''}
                onClick={() => { toggleMode('mip', '1x3'); setShowMode(false); }}
              >MIP</button>
              {viewerMode !== 'stack' && (
                <>
                  <div className={styles.dropdownDivider} />
                  <button onClick={() => { setViewerMode('stack'); setLayout('1x1'); setShowMode(false); }}>
                    Exit to Stack
                  </button>
                </>
              )}
            </div>
          )}
        </div>


        {/* Multi-monitor */}
        <div className={styles.separator} />
        <div className={styles.dropdownContainer}>
          <TB
            icon={<IconMonitor />}
            label="Screens"
            onClick={async () => {
              if (!showMonitors) {
                const s = await getScreens();
                setScreens(s);
              }
              setShowMonitors(!showMonitors);
            }}
          />
          {showMonitors && (
            <div className={styles.dropdown}>
              {screens.length > 1 ? (
                <>
                  {screens.filter((s) => !s.isPrimary).map((s, i) => (
                    <button
                      key={i}
                      onClick={() => {
                        openNewViewerWindow(studyId, undefined, s);
                        setShowMonitors(false);
                      }}
                    >
                      Open on {s.label}
                    </button>
                  ))}
                  <button onClick={() => {
                    openOnAllScreens(studyId);
                    setShowMonitors(false);
                  }}>
                    Open on all screens
                  </button>
                </>
              ) : (
                <button onClick={() => {
                  openNewViewerWindow(studyId);
                  setShowMonitors(false);
                }}>
                  Open new viewer window
                </button>
              )}
            </div>
          )}
        </div>

        {/* More drawer (overflow for less-used tools) */}
        <div className={styles.separator} />
        <div className={styles.dropdownContainer}>
          <TB
            icon={
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="5" cy="12" r="1.5" fill="currentColor" />
                <circle cx="12" cy="12" r="1.5" fill="currentColor" />
                <circle cx="19" cy="12" r="1.5" fill="currentColor" />
              </svg>
            }
            label="More"
            isActive={showMoreDrawer}
            onClick={() => setShowMoreDrawer(!showMoreDrawer)}
          />
          {showMoreDrawer && (
            <div className={styles.moreDrawer}>
              <button
                className={`${styles.moreBtn} ${activeTool === 'Magnify' ? styles.active : ''}`}
                onClick={() => { selectTool('Magnify'); setShowMoreDrawer(false); }}
              ><IconMagnify /><span>Magnify</span></button>
              <button
                className={`${styles.moreBtn} ${activeTool === 'Probe' ? styles.active : ''}`}
                onClick={() => { selectTool('Probe'); setShowMoreDrawer(false); }}
              ><IconProbe /><span>Probe</span></button>
              <button
                className={styles.moreBtn}
                onClick={() => { flipH(); setShowMoreDrawer(false); }}
              ><IconFlipH /><span>Flip H</span></button>
              <button
                className={styles.moreBtn}
                onClick={() => { flipV(); setShowMoreDrawer(false); }}
              ><IconFlipV /><span>Flip V</span></button>
              <button
                className={styles.moreBtn}
                onClick={() => { rotateLeft(); setShowMoreDrawer(false); }}
              ><IconRotateL /><span>Rot L</span></button>
              <button
                className={styles.moreBtn}
                onClick={() => { rotateRight(); setShowMoreDrawer(false); }}
              ><IconRotateR /><span>Rot R</span></button>
              <button
                className={styles.moreBtn}
                onClick={() => { zoomToActual(); setShowMoreDrawer(false); }}
              ><IconZoomActual /><span>1:1</span></button>
              <button
                className={styles.moreBtn}
                onClick={() => { clearAnnotations(); setShowMoreDrawer(false); }}
              ><IconClear /><span>Clear</span></button>
            </div>
          )}
        </div>
      </div>

      {/* ====== Mobile Bottom Bar ====== */}
      <div className={styles.mobileBar}>
        <button className={styles.mobileBtn} onClick={onBack}>
          <IconBack size={20} />
        </button>
        <button className={`${styles.mobileBtn} ${activeTool === 'Wwwc' ? styles.mobileActive : ''}`} onClick={() => selectTool('Wwwc')}>
          <IconWL size={20} />
        </button>
        <button className={`${styles.mobileBtn} ${activeTool === 'Pan' ? styles.mobileActive : ''}`} onClick={() => selectTool('Pan')}>
          <IconPan size={20} />
        </button>
        <button className={`${styles.mobileBtn} ${activeTool === 'Zoom' ? styles.mobileActive : ''}`} onClick={() => selectTool('Zoom')}>
          <IconZoom size={20} />
        </button>
        <button className={`${styles.mobileBtn} ${activeTool === 'StackScroll' ? styles.mobileActive : ''}`} onClick={() => selectTool('StackScroll')}>
          <IconScroll size={20} />
        </button>
        <button className={`${styles.mobileBtn} ${activeTool === 'Length' ? styles.mobileActive : ''}`} onClick={() => selectTool('Length')}>
          <IconLength size={20} />
        </button>
        <button
          className={`${styles.mobileBtn} ${showMoreTools ? styles.mobileActive : ''}`}
          onClick={() => setShowMoreTools(!showMoreTools)}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="5" r="1.5" fill="currentColor" />
            <circle cx="12" cy="12" r="1.5" fill="currentColor" />
            <circle cx="12" cy="19" r="1.5" fill="currentColor" />
          </svg>
        </button>
      </div>

      {/* ====== Mobile More Tools Drawer ====== */}
      {showMoreTools && (
        <div className={styles.mobileDrawerOverlay} onClick={() => setShowMoreTools(false)}>
          <div className={styles.mobileDrawer} onClick={(e) => e.stopPropagation()}>

            <div className={styles.drawerSection}>
              <div className={styles.drawerLabel}>Navigation</div>
              <div className={styles.drawerGrid}>
                <button className={`${styles.drawerBtn} ${activeTool === 'Wwwc' ? styles.active : ''}`} onClick={() => selectToolAndClose('Wwwc')}><IconWL /><span>W/L</span></button>
                <button className={`${styles.drawerBtn} ${activeTool === 'Pan' ? styles.active : ''}`} onClick={() => selectToolAndClose('Pan')}><IconPan /><span>Pan</span></button>
                <button className={`${styles.drawerBtn} ${activeTool === 'Zoom' ? styles.active : ''}`} onClick={() => selectToolAndClose('Zoom')}><IconZoom /><span>Zoom</span></button>
                <button className={`${styles.drawerBtn} ${activeTool === 'StackScroll' ? styles.active : ''}`} onClick={() => selectToolAndClose('StackScroll')}><IconScroll /><span>Scroll</span></button>
                <button className={`${styles.drawerBtn} ${activeTool === 'Magnify' ? styles.active : ''}`} onClick={() => selectToolAndClose('Magnify')}><IconMagnify /><span>Magnify</span></button>
                <button className={`${styles.drawerBtn} ${activeTool === 'Cine' ? styles.active : ''}`} onClick={() => selectToolAndClose('Cine')}><IconCine /><span>Cine</span></button>
              </div>
            </div>

            <div className={styles.drawerSection}>
              <div className={styles.drawerLabel}>Measure</div>
              <div className={styles.drawerGrid}>
                <button className={`${styles.drawerBtn} ${activeTool === 'Length' ? styles.active : ''}`} onClick={() => selectToolAndClose('Length')}><IconLength /><span>Length</span></button>
                <button className={`${styles.drawerBtn} ${activeTool === 'Angle' ? styles.active : ''}`} onClick={() => selectToolAndClose('Angle')}><IconAngle /><span>Angle</span></button>
                <button className={`${styles.drawerBtn} ${activeTool === 'CobbAngle' ? styles.active : ''}`} onClick={() => selectToolAndClose('CobbAngle')} title="Cobb angle - draw two lines along two end-plates; the angle between them is measured"><IconCobb /><span>Cobb</span></button>
                <button className={`${styles.drawerBtn} ${activeTool === 'Probe' ? styles.active : ''}`} onClick={() => selectToolAndClose('Probe')}><IconProbe /><span>Probe</span></button>
                <button className={`${styles.drawerBtn} ${activeTool === 'RectangleROI' ? styles.active : ''}`} onClick={() => selectToolAndClose('RectangleROI')}><IconRect /><span>Rect</span></button>
                <button className={`${styles.drawerBtn} ${activeTool === 'EllipticalROI' ? styles.active : ''}`} onClick={() => selectToolAndClose('EllipticalROI')}><IconEllipse /><span>Ellipse</span></button>
                <button className={`${styles.drawerBtn} ${activeTool === 'ArrowAnnotate' ? styles.active : ''}`} onClick={() => selectToolAndClose('ArrowAnnotate')}><IconArrow /><span>Arrow</span></button>
              </div>
            </div>

            <div className={styles.drawerSection}>
              <div className={styles.drawerLabel}>Image</div>
              <div className={styles.drawerGrid}>
                <button className={styles.drawerBtn} onClick={() => { invertImage(); setShowMoreTools(false); }}><IconInvert /><span>Invert</span></button>
                <button className={styles.drawerBtn} onClick={() => { flipH(); setShowMoreTools(false); }}><IconFlipH /><span>Flip H</span></button>
                <button className={styles.drawerBtn} onClick={() => { flipV(); setShowMoreTools(false); }}><IconFlipV /><span>Flip V</span></button>
                <button className={styles.drawerBtn} onClick={() => { rotateLeft(); setShowMoreTools(false); }}><IconRotateL /><span>Rot L</span></button>
                <button className={styles.drawerBtn} onClick={() => { rotateRight(); setShowMoreTools(false); }}><IconRotateR /><span>Rot R</span></button>
                <button className={styles.drawerBtn} onClick={() => { resetViewport(); setShowMoreTools(false); }}><IconReset /><span>Reset</span></button>
                <button className={styles.drawerBtn} onClick={() => { clearAnnotations(); setShowMoreTools(false); }}><IconClear /><span>Clear</span></button>
                <button className={`${styles.drawerBtn} ${overlaysVisible ? styles.active : ''}`} onClick={() => { toggleOverlays(); setShowMoreTools(false); }}><IconOverlay /><span>Overlay</span></button>
              </div>
            </div>

            <div className={styles.drawerSection}>
              <div className={styles.drawerLabel}>3D / Volume</div>
              <div className={styles.drawerGrid}>
                <button className={`${styles.drawerBtn} ${viewerMode === 'mpr' ? styles.active : ''}`} onClick={() => toggleMode('mpr', '1x3')}><IconMPR /><span>MPR</span></button>
                <button className={`${styles.drawerBtn} ${viewerMode === 'mpr3d' ? styles.active : ''}`} onClick={() => toggleMode('mpr3d', '2x2')}><Icon3D /><span>3D</span></button>
                <button className={`${styles.drawerBtn} ${viewerMode === 'mip' ? styles.active : ''}`} onClick={() => toggleMode('mip', '1x3')}><IconMIP /><span>MIP</span></button>
              </div>
            </div>

          </div>
        </div>
      )}
    </>
  );
}
